import { API_BASE_URL } from '../config/api';

// Types for API responses
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Types for disbursement data
export interface PayoutPreparation {
  landlordId: string;
  availableBalance: number;
  pendingAmount: number;
  totalCollected: number;
  canPayout: boolean;
  nextPayout?: string;
  payoutFrequency: string;
}

export interface WalletTransaction {
  id: string;
  transaction_type: 'CREDIT' | 'DEBIT';
  amount: number;
  balance_after: number;
  description: string;
  created_at: string;
  payment?: {
    id: string;
    amount: number;
    status: string;
    tenant: {
      name: string;
      unit: {
        unitNumber: string;
      };
    };
  };
}

export interface PayoutHistory {
  id: string;
  amount: number;
  status: string;
  created_at: string;
  batch: {
    batch_number: string;
    created_at: string;
  };
}

export interface PendingBatch {
  id: string;
  landlord_id: string;
  status: string;
  created_at: string;
}

export interface DisbursementService {
  getPayoutPreparation(landlordId?: string): Promise<ApiResponse<PayoutPreparation>>;
  getWalletTransactions(landlordId?: string, page?: number, limit?: number): Promise<ApiResponse<{ transactions: WalletTransaction[], pagination: any }>>;
  getPayoutHistory(landlordId?: string, page?: number, limit?: number): Promise<ApiResponse<{ payouts: PayoutHistory[], pagination: any }>>;
  getPayoutFrequency(landlordId?: string): Promise<ApiResponse<{ frequency: string }>>;
  updatePayoutFrequency(landlordId: string | undefined, frequency: string): Promise<ApiResponse<any>>;
  triggerImmediatePayout(landlordId?: string): Promise<ApiResponse<any>>;
  getPendingBatches(): Promise<ApiResponse<PendingBatch[]>>;
}

class DisbursementServiceImpl implements DisbursementService {
  private baseUrl = API_BASE_URL;

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const { authTokenService } = await import('./auth.service');
      const authHeader = authTokenService.getAuthHeader();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };
      if (authHeader) headers['Authorization'] = authHeader;

      const response = await fetch(`${this.baseUrl}/disbursements${endpoint}`, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  /** Org-scoped; landlordId kept for call-site compat (unused). */
  async getPayoutPreparation(_landlordId?: string): Promise<ApiResponse<PayoutPreparation>> {
    return this.makeRequest(`/preparation`);
  }

  async getWalletTransactions(
    _landlordId?: string,
    page: number = 1,
    limit: number = 20
  ): Promise<ApiResponse<{ transactions: WalletTransaction[], pagination: any }>> {
    return this.makeRequest(`/transactions?page=${page}&limit=${limit}`);
  }

  async getPayoutHistory(
    _landlordId?: string,
    page: number = 1,
    limit: number = 10
  ): Promise<ApiResponse<{ payouts: PayoutHistory[], pagination: any }>> {
    return this.makeRequest(`/history?page=${page}&limit=${limit}`);
  }

  async getPayoutFrequency(_landlordId?: string): Promise<ApiResponse<{ frequency: string }>> {
    return this.makeRequest(`/frequency`);
  }

  async updatePayoutFrequency(
    _landlordId: string | undefined,
    frequency: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest(`/frequency`, {
      method: 'PUT',
      body: JSON.stringify({ frequency }),
    });
  }

  async triggerImmediatePayout(_landlordId?: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/payout/immediate`, {
      method: 'POST',
    });
  }

  async getPendingBatches(): Promise<ApiResponse<PendingBatch[]>> {
    return this.makeRequest('/batches/pending');
  }
}

export const disbursementService = new DisbursementServiceImpl();
